import React, { useEffect, useRef, useState } from 'react';
import { mozoApi, mensajeDeError } from './mozoApi';
import {
  claveNueva,
  complemento,
  money,
  normalizarPagos,
  redondear2,
  resumenCobro,
} from './cobroUtils';

const ESTADO_PEDIDO = {
  CONFIRMADO: 'En cocina',
  EN_PREPARACION: 'Preparando',
  LISTO: 'Listo para llevar',
  ENTREGADO: 'Entregado',
  CANCELADO: 'Cancelado',
  BORRADOR: 'Borrador',
};

export default function MozoCuenta({ cuentaId, pedidos, enLinea, onCambio, onMesaLiberada }) {
  const [cuenta, setCuenta] = useState(null);
  const [medios, setMedios] = useState([]);
  const [conPropina, setConPropina] = useState(false);
  const [pagos, setPagos] = useState([{ medio_pago_id: '', monto: '' }]);
  const [confirmando, setConfirmando] = useState(false);
  const [cobrando, setCobrando] = useState(false);
  const [error, setError] = useState('');
  const [entregando, setEntregando] = useState(null);
  const claveCobro = useRef(null);

  const cargarCuenta = async () => {
    try {
      const { data } = await mozoApi.get(`/restaurante/cuentas/${cuentaId}`);

      setCuenta(data);
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo cargar la cuenta.'));
    }
  };

  useEffect(() => {
    let activo = true;

    mozoApi
      .get('/restaurante/medios-pago')
      .then(({ data }) => {
        if (!activo) return;
        const lista = Array.isArray(data) ? data : [];

        setMedios(lista);
        setPagos([{ medio_pago_id: lista[0]?.id ?? '', monto: '' }]);
      })
      .catch((err) => activo && setError(mensajeDeError(err, 'No se pudieron cargar los medios de pago.')));

    return () => {
      activo = false;
    };
  }, []);

  // La cuenta se vuelve a pedir cuando cambian los pedidos de la mesa.
  const firmaPedidos = pedidos.map((p) => `${p.id}:${p.estado}`).join(',');

  useEffect(() => {
    cargarCuenta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cuentaId, firmaPedidos]);

  const resumen = cuenta?.resumen;
  const propinaEditable = resumen?.propina_editable !== false;

  const calculo = resumenCobro({
    consumo: resumen?.consumo_total ?? cuenta?.total,
    pagado: resumen?.pagado,
    propinaPersistida: resumen?.propina_monto,
    propinaEditable,
    conPropina,
  });

  // Con un solo medio, el monto siempre es el saldo.
  useEffect(() => {
    setPagos((actuales) =>
      actuales.length === 1 && calculo.saldo > 0
        ? [{ ...actuales[0], monto: String(calculo.saldo) }]
        : actuales
    );
  }, [calculo.saldo]);

  useEffect(() => {
    setConPropina(redondear2(resumen?.propina_monto) > 0);
  }, [resumen?.propina_monto]);

  const cambiarMedio = (indice, valor) =>
    setPagos((a) => a.map((p, i) => (i === indice ? { ...p, medio_pago_id: valor } : p)));

  const cambiarMonto = (indice, valor) =>
    setPagos((actuales) =>
      actuales.map((pago, i) =>
        i === indice
          ? { ...pago, monto: valor }
          : actuales.length === 2
            ? { ...pago, monto: complemento(valor, calculo.saldo) }
            : pago
      )
    );

  const dividir = () =>
    setPagos((actuales) => [
      { ...actuales[0], monto: '' },
      { medio_pago_id: medios[0]?.id ?? '', monto: '' },
    ]);

  const unSoloMedio = () =>
    setPagos((actuales) => [{ ...actuales[0], monto: String(calculo.saldo) }]);

  const entregar = async (pedidoId) => {
    setEntregando(pedidoId);
    setError('');

    try {
      await mozoApi.post(`/restaurante/pedidos/${pedidoId}/entregado`);
      onCambio?.();
    } catch (err) {
      setError(mensajeDeError(err, 'No se pudo marcar como entregado.'));
    } finally {
      setEntregando(null);
    }
  };

  const cobrar = async () => {
    if (cobrando || !enLinea) return;

    const validado = normalizarPagos(pagos, calculo.saldo);

    if (validado.error) {
      setError(validado.error);
      setConfirmando(false);
      return;
    }

    if (!claveCobro.current) claveCobro.current = claveNueva();

    setCobrando(true);
    setError('');

    try {
      const { data } = await mozoApi.post(`/restaurante/cuentas/${cuentaId}/pagar`, {
        pagos: validado.pagos,
        aplicar_propina: propinaEditable ? conPropina : redondear2(resumen?.propina_monto) > 0,
        clave_idempotencia: claveCobro.current,
      });

      claveCobro.current = null;
      setConfirmando(false);

      if (data?.mesa_liberada) {
        onMesaLiberada?.();
        return;
      }

      await cargarCuenta();
      onCambio?.();
    } catch (err) {
      if (err?.response) claveCobro.current = null;

      setConfirmando(false);
      setError(mensajeDeError(err, 'No se pudo registrar el cobro.'));
    } finally {
      setCobrando(false);
    }
  };

  const vigentes = pedidos.filter((p) => p.estado !== 'CANCELADO');

  return (
    <div className="space-y-4">
      <section className="space-y-3">
        {vigentes.map((pedido) => (
          <article key={pedido.id} className="rounded-xl bg-white p-4 shadow-sm">
            <div className="flex items-center justify-between">
              <p className="text-sm font-extrabold text-gray-700">Pedido #{pedido.id}</p>
              <span
                className={`rounded-full px-2.5 py-1 text-xs font-bold ${
                  pedido.estado === 'LISTO' ? 'bg-amber-200 text-amber-900' : 'bg-gray-100 text-gray-700'
                }`}
              >
                {ESTADO_PEDIDO[pedido.estado] || pedido.estado}
              </span>
            </div>
            <ul className="mt-2 text-sm text-gray-800">
              {pedido.items.map((item) => (
                <li key={item.id} className="flex justify-between gap-2 py-0.5">
                  <span>
                    {item.cantidad} × {item.producto_nombre}
                    {item.observaciones ? ` (${item.observaciones})` : ''}
                  </span>
                  <span className="font-semibold">{money(item.subtotal)}</span>
                </li>
              ))}
            </ul>
            {pedido.estado === 'LISTO' && (
              <button
                type="button"
                onClick={() => entregar(pedido.id)}
                disabled={entregando === pedido.id || !enLinea}
                className="mt-3 w-full rounded-lg bg-amber-400 py-3 text-sm font-extrabold text-gray-900 disabled:opacity-50"
              >
                {entregando === pedido.id ? 'Guardando...' : 'Marcar como entregado'}
              </button>
            )}
          </article>
        ))}
        {!vigentes.length && (
          <p className="py-4 text-center text-sm text-gray-500">Todavía no hay pedidos en esta mesa.</p>
        )}
      </section>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-sm font-bold text-red-700">
          {error}
        </p>
      )}

      {cuenta && (
        <section className="rounded-2xl bg-white p-4 shadow-md">
          <div className="flex justify-between text-base">
            <span className="text-gray-600">Consumo</span>
            <span className="font-bold">{money(calculo.consumo)}</span>
          </div>

          {propinaEditable ? (
            <label className="mt-2 flex items-center justify-between text-base">
              <span className="flex items-center gap-2 text-gray-600">
                <input
                  type="checkbox"
                  checked={conPropina}
                  onChange={(e) => setConPropina(e.target.checked)}
                  className="h-5 w-5"
                />
                Propina 10 %
              </span>
              <span className="font-bold">{money(calculo.propina)}</span>
            </label>
          ) : (
            <div className="mt-2 flex justify-between text-base">
              <span className="text-gray-600">Propina</span>
              <span className="font-bold">{money(calculo.propina)}</span>
            </div>
          )}

          {redondear2(resumen?.pagado) > 0 && (
            <div className="mt-2 flex justify-between text-base">
              <span className="text-gray-600">Ya pagado</span>
              <span className="font-bold">{money(resumen.pagado)}</span>
            </div>
          )}

          <div className="mt-3 flex justify-between border-t pt-3 text-xl font-extrabold">
            <span>Saldo</span>
            <span>{money(calculo.saldo)}</span>
          </div>

          {calculo.saldo > 0 && (
            <div className="mt-4 space-y-3">
              {pagos.map((pago, i) => (
                <div key={i} className="flex gap-2">
                  <select
                    value={pago.medio_pago_id}
                    onChange={(e) => cambiarMedio(i, e.target.value)}
                    aria-label={`Medio de pago ${i + 1}`}
                    className="min-w-0 flex-1 rounded-xl border border-gray-300 px-3 py-3 text-base"
                  >
                    {medios.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.nombre}
                      </option>
                    ))}
                  </select>
                  <input
                    type="number"
                    inputMode="decimal"
                    min="0"
                    step="0.01"
                    value={pago.monto}
                    onChange={(e) => cambiarMonto(i, e.target.value)}
                    aria-label={`Monto ${i + 1}`}
                    readOnly={pagos.length === 1}
                    className="w-32 rounded-xl border border-gray-300 px-3 py-3 text-right text-base"
                  />
                </div>
              ))}

              {pagos.length === 1 ? (
                <button type="button" onClick={dividir} className="text-sm font-bold text-green-800 underline">
                  Dividir con otro medio de pago
                </button>
              ) : (
                <button type="button" onClick={unSoloMedio} className="text-sm font-bold text-green-800 underline">
                  Usar un solo medio
                </button>
              )}

              {!enLinea && (
                <p className="text-sm font-bold text-red-700">
                  Sin conexión: no se puede cobrar hasta que vuelva el wifi.
                </p>
              )}

              {!confirmando ? (
                <button
                  type="button"
                  onClick={() => setConfirmando(true)}
                  disabled={!enLinea}
                  className="w-full rounded-xl bg-green-700 py-4 text-base font-extrabold text-white disabled:opacity-50"
                >
                  Cobrar {money(calculo.saldo)}
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setConfirmando(false)}
                    disabled={cobrando}
                    className="flex-1 rounded-xl bg-gray-200 py-4 text-base font-bold"
                  >
                    Volver
                  </button>
                  <button
                    type="button"
                    onClick={cobrar}
                    disabled={cobrando || !enLinea}
                    className="flex-[2] rounded-xl bg-green-800 py-4 text-base font-extrabold text-white disabled:opacity-50"
                  >
                    {cobrando ? 'Cobrando...' : 'Confirmar cobro'}
                  </button>
                </div>
              )}
            </div>
          )}
        </section>
      )}
    </div>
  );
}
